import type { FilterChip } from "@/types/browser";
import type { WorldInterface } from "@/types/worlds";

// The source filter and the world filter are both multi-select lists of worlds over different
// fields — source matches the element's own `source`, world matches the campaign settings it is
// linked to. They ask different questions but they select the same way, so the selection rules
// live here rather than being answered twice.
//
// Chip keys are built AND parsed here for the same reason: a key format that is written in one
// file and split apart in another is one rename away from a remove button that removes nothing.

export const SOURCE_CHIP_PREFIX = 'source:';
export const WORLD_CHIP_PREFIX = 'world:';

export function worldChips(
    worlds: WorldInterface[],
    prefix: string,
    label: string,
): FilterChip[] {
    return (worlds ?? [])
        .filter((world): world is WorldInterface => !!world?.uuid)
        .map((world) => ({
            key: `${prefix}${world.uuid}`,
            label: `${label}: ${world.name}`,
        }));
}

// Selecting a world already in the list is a no-op, not a second chip. The predicates map the
// list to a uuid IN-clause, where a repeat says nothing but would render a duplicate chip whose
// remove button appears to only half work.
export function appendWorld(
    worlds: WorldInterface[],
    world: WorldInterface | null | undefined,
): WorldInterface[] {
    if (!world?.uuid) return worlds ?? [];
    if ((worlds ?? []).some((existing) => existing?.uuid === world.uuid)) return worlds ?? [];

    return [...(worlds ?? []), world];
}

// Removes only the world its chip stands for — the whole point of multi-select. A chip from
// another filter (different prefix) is ignored rather than silently clearing this one.
export function removeWorldChip(
    worlds: WorldInterface[],
    chip: FilterChip,
    prefix: string,
): WorldInterface[] {
    if (!chip?.key?.startsWith(prefix)) return worlds ?? [];
    const uuid = chip.key.slice(prefix.length);

    return (worlds ?? []).filter((world) => world?.uuid !== uuid);
}
